import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { PublicLayout } from '@/components/Layouts/PublicLayout.tsx';
import { MainLayout } from '@/components/Layouts/MainLayout.tsx';

// Lazy load components
const AuthPage = lazy(() => import('@/features/auth').then((m) => ({ default: m.AuthPage })));
const EmailSubmitPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.EmailSubmitPage })),
);
const SetPassPage = lazy(() => import('@/features/auth').then((m) => ({ default: m.SetPassPage })));
const VerificationPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.VerificationPage })),
);
const SubAccountVerificationPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.SubAccountVerificationPage })),
);

// Admin pages
const DashboardPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.DashboardPage })),
);
const SurveyAdmin = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.SurveyAdmin })),
);
const Clients = lazy(() => import('@/features/admin').then((m) => ({ default: m.Clients })));
const PropertyManagement = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.PropertyManagement })),
);
const AdminSettings = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AdminSettings })),
);
const RegionalSettings = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.RegionalSettings })),
);
const AttributeManagement = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AttributeManagement })),
);
const CategoryUsersPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.CategoryUsersPage })),
);
const WalkthroughRequests = lazy(() =>
  import('@/features/admin/walkthroughs/pages/WalkthroughRequests.tsx').then((m) => ({
    default: m.WalkthroughRequests,
  })),
);
const HelpRequests = lazy(() =>
  import('@/features/admin/help-requests/pages/HelpRequests.tsx').then((m) => ({
    default: m.HelpRequests,
  })),
);
const AdvisorsPage = lazy(() =>
  import('@/features/admin/advisors/pages/AdvisorsPage.tsx').then((m) => ({
    default: m.AdvisorsPage,
  })),
);

// Advisor pages
const AdvisorWalkthroughs = lazy(() =>
  import('@/features/advisor/walkthroughs/pages/AdvisorWalkthroughs.tsx').then((m) => ({
    default: m.AdvisorWalkthroughs,
  })),
);

// Client pages
const Home = lazy(() =>
  import('@/features/client/home/pages/Home.tsx').then((m) => ({ default: m.Home })),
);
const Profile = lazy(() => import('@/features/client').then((m) => ({ default: m.Profile })));
const SurveyClient = lazy(() =>
  import('@/features/client').then((m) => ({ default: m.SurveyClient })),
);
const ClientMaintenanceTasks = lazy(() =>
  import('@/features/client/maintenance-task/pages/ClientMaintenanceTasks.tsx').then((m) => ({
    default: m.ClientMaintenanceTasks,
  })),
);
const CalendarPage = lazy(() =>
  import('@/features/client/calendar/pages/CalendarPage.tsx').then((m) => ({
    default: m.CalendarPage,
  })),
);
const ContactsPage = lazy(() =>
  import('@/features/client/contacts/pages/ContactsPage.tsx').then((m) => ({
    default: m.ContactsPage,
  })),
);
const NotificationsSettingsPage = lazy(() =>
  import('@/features/client/notifications/pages/NotificationsSettingsPage.tsx').then((m) => ({
    default: m.NotificationsSettingsPage,
  })),
);
const DocumentationPage = lazy(() =>
  import('@/features/client/documentation/pages/DocumentationPage.tsx').then((m) => ({
    default: m.DocumentationPage,
  })),
);
const DocumentsPage = lazy(() =>
  import('@/features/client/documents/pages/DocumentsPage.tsx').then((m) => ({
    default: m.DocumentsPage,
  })),
);
const HelpPage = lazy(() =>
  import('@/features/client/help/pages/HelpPage.tsx').then((m) => ({
    default: m.HelpPage,
  })),
);

// Loading fallback component
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size='large' />
  </div>
);

// Wrap lazy components with Suspense
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

export const routes = [
  {
    path: '/public',
    element: <PublicLayout />,
    children: [
      { id: 'login', path: 'login', element: withSuspense(AuthPage) },
      { id: 'email-reset', path: 'email-reset', element: withSuspense(EmailSubmitPage) },
      { id: 'set-pass', path: 'set-pass', element: withSuspense(SetPassPage) },
      { id: 'verification', path: 'verification', element: withSuspense(VerificationPage) },
      {
        id: 'sub-account-verification',
        path: 'sub-account-verification',
        element: withSuspense(SubAccountVerificationPage),
      },
      { id: 'help', path: 'help', element: withSuspense(DocumentationPage) },
    ],
    roles: [],
  },
  {
    path: '/',
    element: <MainLayout />,
    children: [
      { index: true, element: withSuspense(DashboardPage) },
      { path: 'insights/:category', element: withSuspense(CategoryUsersPage) },
      { path: 'survey-management', element: withSuspense(SurveyAdmin) },
      {
        path: 'clients',
        children: [
          { index: true, element: withSuspense(Clients) },
          { path: 'property-management', element: withSuspense(PropertyManagement) },
        ],
      },
      { path: 'advisors', element: withSuspense(AdvisorsPage) },
      { path: 'walkthroughs', element: withSuspense(WalkthroughRequests) },
      { path: 'help-requests', element: withSuspense(HelpRequests) },
      { path: 'settings', element: withSuspense(AdminSettings) },
      { path: 'regional-settings', element: withSuspense(RegionalSettings) },
      { path: 'attributes', element: withSuspense(AttributeManagement) },
    ],
    roles: ['ADMIN'],
  },
  {
    path: '/client',
    element: <MainLayout />,
    children: [
      { index: true, element: withSuspense(Home) },
      { path: 'maintenance-tasks', element: withSuspense(ClientMaintenanceTasks) },
      { path: 'profile', element: withSuspense(Profile) },
      { path: 'survey', element: withSuspense(SurveyClient) },
      { path: 'calendar', element: withSuspense(CalendarPage) },
      { path: 'contacts', element: withSuspense(ContactsPage) },
      { path: 'notifications', element: withSuspense(NotificationsSettingsPage) },
      { path: 'documents', element: withSuspense(DocumentsPage) },
      { path: 'help', element: withSuspense(HelpPage) },
    ],
    roles: ['CLIENT'],
  },
  {
    path: '/advisor',
    element: <MainLayout />,
    children: [
      {
        path: 'clients',
        children: [
          { index: true, element: withSuspense(Clients) },
          { path: 'property-management', element: withSuspense(PropertyManagement) },
        ],
      },
      { path: 'walkthroughs', element: withSuspense(AdvisorWalkthroughs) },
    ],
    roles: ['ADVISOR'],
  },
  {
    path: '*',
    element: <Navigate to='/public/login' replace />,
    roles: [],
  },
];
